import classNames from 'classnames';
import { DecoratorNode, NodeKey, SerializedLexicalNode } from 'lexical';
import { isBuiltinVariableName, isMetadataVariableName } from '../availableVariables';
import { ExternalAccessToken } from '../component/ParameterDropdown';
import { ParameterDto } from '@/Domain/api/Action/dto/Action.dto';

export enum boxStyle {
  CURLY = 'curly',
  SQUARE = 'square',
}

export interface SerializedBadgeNode extends SerializedLexicalNode {
  label: string;
  parameter_id: number | null;
  style: boxStyle;
}

export class BadgeNode extends DecoratorNode<JSX.Element> {
  __label: string;
  __parameterId: number;
  __style: boxStyle;

  constructor(label: string, parameterId: number, style: boxStyle, key?: NodeKey) {
    super(key);
    this.__label = label.replaceAll(' ', '_');
    this.__parameterId = parameterId;
    this.__style = style;
  }

  static parameters: ParameterDto[] = [];

  static getType() {
    return 'badge';
  }

  getLabel() {
    return this.__style == boxStyle.CURLY ? `{{${this.__label}}}` : `[[${this.__label}]]`;
  }

  static clone(node: BadgeNode) {
    return new BadgeNode(node.__label, node.__parameterId, node.__style, node.__key);
  }

  static cloneWithoutKey(node: BadgeNode) {
    return new BadgeNode(node.__label, node.__parameterId, node.__style);
  }

  static importJSON(serializedNode: SerializedBadgeNode): BadgeNode {
    const { label, parameter_id, style } = serializedNode;
    return new BadgeNode(label, parameter_id ?? -1, style);
  }

  static setParameters(params: ParameterDto[]) {
    BadgeNode.parameters = params;
  }

  createDOM(): HTMLElement {
    return document.createElement('span');
  }

  updateDOM(): boolean {
    return false;
  }

  update() {
    return false;
  }

  exportJSON() {
    return {
      type: 'badge',
      version: 1,
      label: this.__label,
      parameter_id: this.__parameterId == -1 ? null : this.__parameterId,
      style: this.__style,
    };
  }

  decorate(): JSX.Element {
    const label = this.__label.replaceAll(' ', '_');
    const param = BadgeNode.parameters.find(
      (p) => p.name.replaceAll(/ /g, '_').toLowerCase() === label.toLowerCase(),
    );
    const style = this.__style;

    // Check if this is a system token (e.g., HubSpot access token)
    // Uses enum values to support future external access tokens
    const isSystemToken = Object.values(ExternalAccessToken).some(
      (token) => label.toLowerCase() === token.toLowerCase(),
    );
    const isBuiltin = isBuiltinVariableName(label);
    const isMetadata = isMetadataVariableName(label);
    const isCurly = style == boxStyle.CURLY;
    const isKnown = Boolean(param) || isBuiltin || isMetadata || isSystemToken;

    return (
      <span
        className={classNames(
          'inline-flex items-center text-xs font-semibold px-1.5 py-0.5 mx-0.5  border  rounded-md',
          {
            // Orange: HubSpot system tokens, [[..]] legacy metadata, and {{metadata_*}}
            'bg-orange-100 text-orange-700 border-orange-500':
              isSystemToken || style == boxStyle.SQUARE || (isCurly && isMetadata),
            // Blue: user-defined parameter or built-in variable
            'bg-primary-25 text-primary-600 border-primary-100':
              isCurly && !isSystemToken && !isMetadata && (Boolean(param) || isBuiltin),
            // Red: nothing matches — likely a typo
            'bg-red-100 text-red-700 border-red-500': isCurly && !isKnown,
            // Optional defined parameter - dashed border
            '!border-dashed': param && !param.is_required,
          },
        )}
      >
        {this.getLabel()}
      </span>
    );
  }
}
